import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Loader2, AlertTriangle, Clock, TrendingUp, CheckCircle2, Bell, School as SchoolIcon, FileText, Check, X as XIcon } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { getAlertsList } from '../api_extra';

const SEV_META = {
  critical: { pill: 'pill-red',   icon: AlertTriangle, tint: '#EF4444', label: 'حرج' },
  warning:  { pill: 'pill-amber', icon: Clock,         tint: '#F59E0B', label: 'تحذير' },
  info:     { pill: 'pill-blue',  icon: TrendingUp,    tint: '#3B82F6', label: 'معلومة' },
  success:  { pill: 'pill-green', icon: CheckCircle2,  tint: '#22C55E', label: 'نجاح' },
};

const CATEGORY_LABELS = {
  delayed_school: 'مدرسة متأخرة',
  high_priority_ticket: 'أولوية حرجة',
  aging_ticket: 'بلاغ متقادم',
  contractor_performance: 'أداء المقاول',
  school_health: 'صحة المدرسة',
  operational: 'ملخص تشغيلي',
};

function readReviewed() {
  try { return JSON.parse(localStorage.getItem('tbc_reviewed_alerts_v1') || '[]'); } catch (e) { return []; }
}
function writeReviewed(list) {
  try { localStorage.setItem('tbc_reviewed_alerts_v1', JSON.stringify(list)); } catch (e) {}
}

export default function AlertsPage() {
  const nav = useNavigate();
  const [severity, setSeverity] = useState('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewed, setReviewed] = useState(readReviewed());

  useEffect(() => {
    // Load previously stored dashboard filters
    let filters = {};
    try {
      const raw = localStorage.getItem('tbc_dashboard_filters_v2');
      if (raw) filters = JSON.parse(raw);
    } catch (e) {}

    let alive = true;
    setLoading(true); setError(null);
    getAlertsList({ severity, search, filters })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [severity, search]);

  const items = data?.items || [];
  const counts = data?.counts || { critical: 0, warning: 0, info: 0, success: 0 };

  const toggleReviewed = (id) => {
    const key = String(id);
    const next = reviewed.includes(key) ? reviewed.filter((x) => x !== key) : [...reviewed, key];
    setReviewed(next); writeReviewed(next);
  };

  const clearReviewed = () => { setReviewed([]); writeReviewed([]); };

  return (
    <div dir="rtl" className="min-h-screen bg-[#0A101C] flex">
      <Sidebar />
      <main className="flex-1 p-4 min-w-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-slate-400 text-[11px] font-medium">لوحة القيادة › مركز التنبيهات</div>
            <h1 className="text-white text-[22px] font-extrabold tracking-tight leading-tight mt-0.5">مركز التنبيهات</h1>
            <div className="text-slate-400 text-[12px] mt-0.5">جميع التنبيهات المُستنتجة من البيانات المستوردة</div>
          </div>
          <button onClick={() => nav('/')} className="panel panel-hoverable px-3 py-2 flex items-center gap-2 text-slate-200 text-[12.5px] font-semibold">
            <ArrowRight size={14} /> العودة إلى لوحة التحكم
          </button>
        </div>

        {/* Severity summary tabs */}
        <div className="grid grid-cols-5 gap-2 mb-3">
          <button onClick={() => setSeverity('all')}
            className={`panel panel-hoverable px-3 py-2.5 flex items-center gap-2 text-right ${severity === 'all' ? 'ring-2 ring-blue-500/50' : ''}`}>
            <div className="icon-chip" style={{ background: 'rgba(59,130,246,0.14)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.25)', width: 30, height: 30 }}><Bell size={14} /></div>
            <div><div className="text-slate-400 text-[10.5px] font-semibold">الكل</div>
              <div className="text-white text-[15px] font-extrabold tabular-nums">{(counts.critical + counts.warning + counts.info + counts.success).toLocaleString('en-US')}</div>
            </div>
          </button>
          {['critical', 'warning', 'info', 'success'].map((s) => {
            const m = SEV_META[s];
            const Ic = m.icon;
            return (
              <button key={s} onClick={() => setSeverity(s)}
                className={`panel panel-hoverable px-3 py-2.5 flex items-center gap-2 text-right ${severity === s ? 'ring-2' : ''}`}
                style={severity === s ? { boxShadow: `0 0 0 2px ${m.tint}55` } : {}}>
                <div className="icon-chip" style={{ background: `${m.tint}18`, color: m.tint, border: `1px solid ${m.tint}30`, width: 30, height: 30 }}><Ic size={14} /></div>
                <div>
                  <div className="text-slate-400 text-[10.5px] font-semibold">{m.label}</div>
                  <div className="text-white text-[15px] font-extrabold tabular-nums">{(counts[s] || 0).toLocaleString('en-US')}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Search bar + clear reviewed */}
        <div className="panel p-3 mb-3 flex items-center gap-2">
          <div className="bg-[#0B1424] border border-white/10 hover:border-white/25 rounded-lg px-2.5 py-2 flex items-center gap-2 flex-1 transition-colors">
            <Search size={14} className="text-slate-400 shrink-0" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث في التنبيهات..."
              className="bg-transparent outline-none text-slate-100 text-[13px] flex-1 min-w-0 text-right placeholder:text-slate-500" />
            {search && (
              <button onClick={() => setSearch('')} className="text-slate-500 hover:text-red-400"><XIcon size={13} /></button>
            )}
          </div>
          <button onClick={clearReviewed} className="panel panel-hoverable px-3 py-2 text-slate-300 text-[12.5px]">
            إعادة تعيين "تمت المراجعة"
          </button>
        </div>

        {/* Alerts list */}
        <div className="panel p-0 overflow-hidden">
          {loading && (
            <div className="p-8 flex items-center justify-center gap-2 text-slate-300">
              <Loader2 size={16} className="animate-spin text-blue-400" /> جاري تحميل التنبيهات...
            </div>
          )}
          {error && <div className="p-4 text-red-300 text-sm">خطأ: {error}</div>}
          {!loading && items.length === 0 && (
            <div className="py-14 text-center text-slate-500">لا توجد تنبيهات مطابقة</div>
          )}
          <div className="divide-y divide-white/[0.04]">
            {items.map((a) => {
              const m = SEV_META[a.severity] || SEV_META.info;
              const Ic = m.icon;
              const isReviewed = reviewed.includes(String(a.id));
              return (
                <div key={a.id}
                  onClick={() => {
                    if (a.school_id) nav(`/school/${encodeURIComponent(a.school_id)}`);
                  }}
                  className={`px-4 py-3 flex items-start gap-3 hover:bg-white/[0.04] transition-colors ${a.school_id ? 'cursor-pointer' : ''} ${isReviewed ? 'opacity-55' : ''}`}>
                  <div className="icon-chip shrink-0 mt-0.5" style={{ background: `${m.tint}18`, color: m.tint, border: `1px solid ${m.tint}30`, width: 34, height: 34 }}>
                    <Ic size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`pill ${m.pill}`}>{m.label}</span>
                      <span className="pill">{CATEGORY_LABELS[a.category] || a.category}</span>
                      {a.timestamp && (
                        <span className="text-slate-500 text-[11px] tabular-nums">
                          {new Date(a.timestamp).toLocaleDateString('en-GB')}
                        </span>
                      )}
                    </div>
                    <div className="text-slate-100 text-[13px] font-bold mt-1 truncate" title={a.title}>{a.title}</div>
                    <div className="text-slate-400 text-[12px] mt-0.5">{a.desc}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {a.school_id && (
                      <button onClick={() => nav(`/school/${encodeURIComponent(a.school_id)}`)}
                        className="panel panel-hoverable px-2.5 py-1.5 text-[11.5px] text-slate-200 font-semibold flex items-center gap-1" title="فتح المدرسة">
                        <SchoolIcon size={13} /> المدرسة
                      </button>
                    )}
                    {a.ticket_id && a.school_id && (
                      <button onClick={() => nav(`/school/${encodeURIComponent(a.school_id)}`)}
                        className="panel panel-hoverable px-2.5 py-1.5 text-[11.5px] text-slate-200 font-semibold flex items-center gap-1" title="فتح البلاغ">
                        <FileText size={13} /> البلاغ
                      </button>
                    )}
                    <button onClick={() => toggleReviewed(a.id)}
                      className={`px-2.5 py-1.5 rounded-lg text-[11.5px] font-semibold flex items-center gap-1 transition-colors border ${isReviewed ? 'bg-green-500/15 border-green-500/30 text-green-300' : 'bg-transparent border-white/10 text-slate-300 hover:bg-white/5'}`}
                      title={isReviewed ? 'تراجع عن المراجعة' : 'تحديد كمراجَع'}>
                      <Check size={13} /> {isReviewed ? 'تمت المراجعة' : 'مراجَع'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
